import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';

// Importações do Leaflet com tratamento de erro
let L: any;
let leafletLoaded = false;

try {
  // Importar o Leaflet e seus estilos
  L = require('leaflet');
  require('leaflet/dist/leaflet.css');
  
  // Verificar se o Leaflet foi carregado corretamente
  if (!L || typeof L.map !== 'function') {
    throw new Error('Leaflet não foi carregado corretamente');
  }
  
  // Tentar importar o Leaflet Routing Machine
  try {
    require('leaflet-routing-machine');
    require('leaflet-routing-machine/dist/leaflet-routing-machine.css');
    
    // Verificar se o Leaflet Routing Machine foi carregado corretamente
    if (!L.Routing || typeof L.Routing.control !== 'function') {
      console.warn('Leaflet Routing Machine não foi carregado corretamente, algumas funcionalidades podem não estar disponíveis');
    } else {
      console.log('Leaflet Routing Machine carregado com sucesso');
    }
  } catch (routingError) {
    console.warn('Erro ao carregar o Leaflet Routing Machine:', routingError);
    console.warn('Algumas funcionalidades de rota podem não estar disponíveis');
  }
  
  leafletLoaded = true;
  console.log('Leaflet carregado com sucesso');
} catch (e) {
  console.error('Erro ao carregar o Leaflet:', e);
  leafletLoaded = false;
}

// Tipos para o componente
interface Location {
  lat: number;
  lng: number;
}

interface Waypoint {
  id: string;
  title: string;
  address?: string;
  location: Location;
  source?: string;
}

interface RouteMapComponentProps {
  waypoints?: Waypoint[];
  directions?: any[];
  center?: Location;
  zoom?: number;
  startLocation?: Location;
}

/**
 * Componente de mapa para exibir a rota otimizada
 */
const RouteMapComponent: React.FC<RouteMapComponentProps> = ({ 
  waypoints = [], 
  directions = [],
  center, 
  zoom = 10,
  startLocation
}) => {
  // Refs para elementos DOM e instâncias do Leaflet
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const routingControlRef = useRef<any>(null);
  const layerGroupRef = useRef<any>(null);
  
  // Estados para controle de UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [mapInitialized, setMapInitialized] = useState(false);
  const [useSimpleMap, setUseSimpleMap] = useState(false); // Estado para controlar quando usar o SimpleRouteMap

  // Verificar se o Leaflet foi carregado corretamente
  useEffect(() => {
    if (!leafletLoaded) {
      setError('Não foi possível carregar a biblioteca de mapas.');
      setLoading(false);
    }
  }, []);

  // Inicializar ícones do Leaflet de forma segura
  const initializeIcons = () => {
    if (!leafletLoaded || !L) return { startIcon: null, stopIcon: null };
    
    try {
      // Corrigir o problema dos ícones do Leaflet
      if (L.Icon && L.Icon.Default && L.Icon.Default.prototype) {
        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
          iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
        });
      }

      // Ícones personalizados
      const startIcon = new L.Icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });

      const stopIcon = new L.Icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });

      return { startIcon, stopIcon };
    } catch (e) {
      console.error('Erro ao inicializar ícones:', e);
      return { startIcon: null, stopIcon: null };
    }
  };

  // Calcular o centro do mapa
  const mapCenter = React.useMemo(() => {
    // Centro padrão (Brasília)
    const defaultCenter = { lat: -15.7801, lng: -47.9292 };
    
    if (center) return center;
    if (startLocation) return startLocation;
    if (waypoints.length > 0 && waypoints[0].location) return waypoints[0].location;
    
    return defaultCenter;
  }, [center, startLocation, waypoints]);

  // Função para limpar recursos do mapa de forma segura
  const cleanupMapResources = () => {
    // Verificar se o mapa está inicializado
    const mapExists = mapInstanceRef.current && 
                    typeof mapInstanceRef.current.remove === 'function' && 
                    typeof mapInstanceRef.current._leaflet_id !== 'undefined';
    
    // Limpar marcadores
    if (markersRef.current && markersRef.current.length > 0) {
      markersRef.current.forEach(marker => {
        if (marker && typeof marker.remove === 'function') {
          try {
            marker.remove();
          } catch (e) {
            console.warn('Erro ao remover marcador:', e);
          }
        }
      });
      markersRef.current = [];
    }
    
    // Limpar grupo de camadas
    if (layerGroupRef.current) {
      try {
        // Primeiro, limpar as camadas
        if (typeof layerGroupRef.current.clearLayers === 'function') {
          layerGroupRef.current.clearLayers();
        }
        
        // Depois, remover o grupo do mapa se o mapa existir
        if (mapExists && 
            typeof mapInstanceRef.current.removeLayer === 'function' && 
            typeof layerGroupRef.current._leaflet_id !== 'undefined') {
          mapInstanceRef.current.removeLayer(layerGroupRef.current);
        }
      } catch (e) {
        console.warn('Erro ao limpar grupo de camadas:', e);
      }
      layerGroupRef.current = null;
    }
    
    // Limpar controle de rota
    if (routingControlRef.current) {
      try {
        // Remover o controle do mapa se o mapa existir
        if (mapExists && typeof mapInstanceRef.current.removeControl === 'function') {
          mapInstanceRef.current.removeControl(routingControlRef.current);
        }
      } catch (e) {
        console.warn('Erro ao remover controle de rota:', e);
      }
      routingControlRef.current = null;
    }
  };

  // Inicializar e atualizar o mapa
  useEffect(() => {
    console.log('useEffect para inicialização do mapa executado');
    
    // Resetar o estado de inicialização do mapa quando os waypoints ou o centro mudam
    setMapInitialized(false);
    
    // Verificar se temos o DOM e o Leaflet
    if (!mapRef.current || !leafletLoaded || !L) {
      setLoading(false);
      if (leafletLoaded && !L) {
        setError('Biblioteca de mapas não disponível.');
      }
      return;
    }
    
    console.log('Condições iniciais verificadas, prosseguindo com a inicialização do mapa');

    // Inicializar ícones
    const { startIcon, stopIcon } = initializeIcons();
    if (!startIcon || !stopIcon) {
      setError('Não foi possível inicializar os ícones do mapa.');
      setLoading(false);
      return;
    }

    try {
      // Limpar recursos existentes antes de atualizar
      cleanupMapResources();
      
      // Verificar se o elemento DOM do mapa existe
      if (!mapRef.current) {
        console.error('Elemento DOM do mapa não encontrado');
        setError('Elemento do mapa não encontrado.');
        setLoading(false);
        return;
      }
      
      // Criar ou atualizar a instância do mapa
      try {
        if (!mapInstanceRef.current) {
          console.log('Criando nova instância do mapa...');
          // Garantir que o elemento não tenha um mapa associado
          // Usamos any para acessar propriedades internas do Leaflet
          const mapElement = mapRef.current as any;
          if (mapElement && mapElement._leaflet_id) {
            console.warn('Elemento já tem um mapa associado, limpando...');
            mapElement._leaflet_id = null;
          }
          
          // Criar nova instância do mapa com tratamento de erro
          mapInstanceRef.current = L.map(mapRef.current, {
            center: [mapCenter.lat || 0, mapCenter.lng || 0],
            zoom: zoom || 10,
            layers: [L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            })]
          });
          
          // Adicionar handler para erros de carregamento de tiles
          try {
            // No Leaflet, não existe um método getLayers() no objeto do mapa
            // Em vez disso, podemos acessar as camadas base e sobreposições
            // Vamos tentar encontrar a camada de tiles de forma segura
            const mapInstance = mapInstanceRef.current as any;
            
            // Verificar se temos acesso às camadas
            if (mapInstance && mapInstance._layers) {
              // Procurar por uma camada de tiles nas camadas existentes
              const layers = mapInstance._layers;
              for (const layerId in layers) {
                if (layers[layerId] instanceof L.TileLayer) {
                  const tileLayer = layers[layerId];
                  tileLayer.on('tileerror', (error: any) => {
                    console.warn('Erro ao carregar tile:', error);
                  });
                  console.log('Handler para erros de tiles adicionado');
                  break;
                }
              }
            }
          } catch (tileError) {
            // Não falhar se não conseguirmos adicionar o handler
            console.warn('Não foi possível adicionar handler para erros de tiles:', tileError);
          }
          
          // Tentar adicionar um evento para garantir que o mapa está completamente carregado
          try {
            // O evento 'load' pode não existir em todas as versões do Leaflet
            if (typeof mapInstanceRef.current.once === 'function') {
              mapInstanceRef.current.once('load', function() {
                console.log('Evento load do mapa disparado');
                setMapInitialized(true);
              });
            } else {
              console.log('Método once não disponível, usando alternativa');
              // Alternativa: verificar se o mapa tem um container válido
              if (mapInstanceRef.current._container) {
                console.log('Mapa tem container válido, considerando inicializado');
                setMapInitialized(true);
              }
            }
          } catch (eventError) {
            console.warn('Erro ao configurar evento load:', eventError);
          }
          
          // Também definir como inicializado após um pequeno delay como backup
          setTimeout(() => {
            console.log('Verificando inicialização do mapa após timeout');
            if (!mapInitialized && mapInstanceRef.current) {
              console.log('Definindo mapa como inicializado após timeout');
              setMapInitialized(true);
            }
          }, 1000);
          
          // Definir como inicializado imediatamente se o mapa tiver um container válido
          if (mapInstanceRef.current && mapInstanceRef.current._container) {
            console.log('Mapa tem container válido na inicialização, considerando inicializado');
            setMapInitialized(true);
          }
          
          console.log('Mapa criado com sucesso');
        } else if (mapInstanceRef.current && typeof mapInstanceRef.current.setView === 'function') {
          // Atualizar visualização do mapa existente
          console.log('Atualizando visualização do mapa existente...');
          mapInstanceRef.current.setView([mapCenter.lat || 0, mapCenter.lng || 0], zoom || 10);
        }
      } catch (mapError) {
        console.error('Erro ao criar/atualizar mapa:', mapError);
        // @ts-ignore
        setError(`Erro ao inicializar mapa: ${mapError.message || 'Erro desconhecido'}`);
        setLoading(false);
        return;
      }

      // Verificar se o mapa foi inicializado corretamente antes de continuar
      if (!mapInstanceRef.current || typeof mapInstanceRef.current.addLayer !== 'function') {
        console.error('Instância do mapa inválida ou não inicializada corretamente');
        setError('Mapa não inicializado corretamente.');
        setLoading(false);
        return;
      }

      // Criar grupo de camadas para organizar elementos do mapa com tratamento de erro
      try {
        layerGroupRef.current = L.layerGroup();
        mapInstanceRef.current.addLayer(layerGroupRef.current);
        console.log('Grupo de camadas criado e adicionado ao mapa');
      } catch (layerError) {
        console.error('Erro ao criar grupo de camadas:', layerError);
        // @ts-ignore
        setError(`Erro ao criar camadas do mapa: ${layerError.message || 'Erro desconhecido'}`);
        setLoading(false);
        return;
      }

      // Filtrar waypoints válidos
      const validWaypoints = (waypoints || []).filter(wp => (
        wp && wp.location && 
        typeof wp.location.lat === 'number' && 
        !isNaN(wp.location.lat) &&
        typeof wp.location.lng === 'number' &&
        !isNaN(wp.location.lng)
      ));
      
      console.log(`Processando ${validWaypoints.length} waypoints válidos`);
      
      if (validWaypoints.length > 0) {
        // Adicionar marcadores para cada waypoint válido
        validWaypoints.forEach((waypoint, index) => {
          try {
            const isStart = index === 0;
            const lat = parseFloat(waypoint.location.lat.toString());
            const lng = parseFloat(waypoint.location.lng.toString());
            
            // Verificar se as coordenadas são válidas
            if (isNaN(lat) || isNaN(lng)) {
              console.warn(`Coordenadas inválidas para waypoint ${index}:`, waypoint.location);
              return; // Pular este waypoint
            }
            
            // Criar e adicionar o marcador
            const marker = L.marker(
              [lat, lng],
              { icon: isStart ? startIcon : stopIcon }
            );
            
            // Adicionar o marcador ao grupo de camadas
            if (layerGroupRef.current && typeof layerGroupRef.current.addLayer === 'function') {
              layerGroupRef.current.addLayer(marker);
            }
            
            // Configurar popup com informações do waypoint
            const title = waypoint.title || `Ponto ${index + 1}`;
            const address = waypoint.address || '';
            marker.bindPopup(`<b>${title}</b>${address ? `<br>${address}` : ''}`);
            
            // Armazenar referência ao marcador
            markersRef.current.push(marker);
            
            console.log(`Marcador adicionado para waypoint ${index}: ${title}`);
          } catch (markerError) {
            console.warn(`Erro ao adicionar marcador para waypoint ${index}:`, markerError);
          }
        });

        // Criar rota entre pontos se tivermos pelo menos 2 waypoints válidos
        if (validWaypoints.length >= 2) {
          try {
            // Verificar se o Leaflet Routing Machine está disponível
            if (!L || !L.Routing || typeof L.Routing.control !== 'function') {
              console.warn('L.Routing.control não está disponível');
              return;
            }
            
            // Verificar se o Leaflet está carregado corretamente
            if (!leafletLoaded) {
              console.warn('Leaflet não foi carregado corretamente');
              return;
            }
            
            // Criar pontos da rota a partir dos waypoints válidos
            const routePoints = validWaypoints.map(wp => {
              const lat = parseFloat(wp.location.lat.toString());
              const lng = parseFloat(wp.location.lng.toString());
              return L.latLng(lat, lng);
            }).filter(point => !isNaN(point.lat) && !isNaN(point.lng));
            
            // Logar os pontos da rota para depuração
            console.log('Pontos da rota:');
            routePoints.forEach((point, index) => {
              console.log(`Ponto ${index}: lat=${point.lat.toFixed(6)}, lng=${point.lng.toFixed(6)}`);
            });
            
            if (routePoints.length < 2) {
              console.warn('Não há pontos válidos suficientes para criar uma rota');
              return;
            }
            
            console.log(`Criando rota com ${routePoints.length} pontos`);
            
            // Indicar que a rota está carregando
            setRouteLoading(true);
            setRouteError('');
            
            // Criar um timeout para detectar quando o carregamento da rota está demorando muito
            const routeTimeout = setTimeout(() => {
              console.warn('Timeout ao calcular rota. Exibindo apenas os marcadores.');
              setRouteLoading(false);
              setRouteError('O cálculo da rota está demorando muito. Exibindo apenas os marcadores.');
              // Se chegamos aqui, o cálculo da rota está demorando muito ou falhou
              // Vamos garantir que pelo menos os marcadores sejam exibidos
              if (mapInstanceRef.current && typeof mapInstanceRef.current.fitBounds === 'function') {
                try {
                  const bounds = L.latLngBounds(routePoints);
                  if (bounds.isValid()) {
                    mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
                    console.log('Zoom ajustado para mostrar todos os pontos (sem rota)');
                  }
                } catch (e) {
                  console.warn('Erro ao ajustar zoom para os pontos:', e);
                }
              }
            }, 10000); // 10 segundos de timeout
            
            // Tentar duas abordagens para calcular a rota
            // 1. Primeiro, tentar desenhar uma linha reta entre os pontos (como backup)
            try {
              console.log('Desenhando linha reta entre os pontos como backup...');
              const polyline = L.polyline(routePoints, {
                color: '#FF5733',
                weight: 3,
                opacity: 0.5,
                dashArray: '5, 10',
                lineJoin: 'round'
              }).addTo(mapInstanceRef.current);
              
              // Adicionar a polyline ao layerGroup para poder removê-la depois
              if (layerGroupRef.current && typeof layerGroupRef.current.addLayer === 'function') {
                layerGroupRef.current.addLayer(polyline);
              }
              
              console.log('Linha reta de backup desenhada com sucesso');
            } catch (polylineError) {
              console.error('Erro ao desenhar linha reta de backup:', polylineError);
            }
            
            // 2. Agora, tentar usar o Leaflet Routing Machine
            console.log('Tentando calcular rota com Leaflet Routing Machine...');
            try {
              // Verificar qual serviço de roteamento usar
              // Tentar primeiro com o OpenRouteService que é mais confiável
              let router;
              
              // Verificar se o OSRM está disponível
              if (L.Routing && typeof L.Routing.osrm === 'function') {
                console.log('Usando OSRM para calcular a rota');
                
                // Tentar com diferentes serviços OSRM
                try {
                  router = L.Routing.osrm({
                    serviceUrl: 'https://router.project-osrm.org/route/v1',
                    profile: 'driving',
                    timeout: 30000,
                    suppressDemoServerWarning: true
                  });
                } catch (osrmError) {
                  console.warn('Erro ao criar roteador OSRM:', osrmError);
                  
                  // Tentar com uma implementação personalizada
                  console.log('Criando roteador personalizado...');
                  router = {
                    // @ts-ignore
                    route: function(waypoints: any, callback: any, context: any, options: any) {
                      console.log('Usando roteador personalizado');
                      // Simular uma resposta de rota com uma linha reta
                      const coordinates = waypoints.map((wp: any) => [wp.latLng.lat, wp.latLng.lng]);
                      
                      // Criar uma resposta simulada
                      setTimeout(() => {
                        callback.call(context, null, [{
                          name: 'Rota direta',
                          coordinates: coordinates,
                          instructions: [],
                          summary: { totalDistance: 0, totalTime: 0 },
                          inputWaypoints: waypoints,
                          waypoints: waypoints
                        }]);
                      }, 500);
                    }
                  };
                }
              } 
              // Caso contrário, usar um roteador personalizado
              else {
                console.log('OSRM não disponível, usando roteador personalizado');
                router = {
                  // @ts-ignore
                  route: function(waypoints: any[], callback: (error: any, routes: any) => void, context: any, options: any) {
                    console.log('Usando roteador personalizado');
                    // Simular uma resposta de rota com uma linha reta
                    const coordinates = waypoints.map((wp: any) => [wp.latLng.lat, wp.latLng.lng]);
                    
                    // Criar uma resposta simulada
                    setTimeout(() => {
                      callback.call(context, null, [{
                        name: 'Rota direta',
                        coordinates: coordinates,
                        instructions: [],
                        summary: { totalDistance: 0, totalTime: 0 },
                        inputWaypoints: waypoints,
                        waypoints: waypoints
                      }]);
                    }, 500);
                  }
                };
              }
              
              // Criar controle de rota com opções mais robustas
              routingControlRef.current = L.Routing.control({
                waypoints: routePoints,
                router: router,
                lineOptions: {
                  styles: [{ color: '#3388ff', weight: 6, opacity: 0.7 }],
                  extendToWaypoints: true,
                  missingRouteTolerance: 100
                },
                createMarker: function() { return null; }, // Não criar marcadores automáticos
                fitSelectedRoutes: true,
                showAlternatives: false,
                altLineOptions: {
                  styles: [
                    { color: '#4169E1', opacity: 0.6, weight: 4 },
                    { color: '#1E90FF', opacity: 0.6, weight: 4 }
                  ]
                },
                collapsible: true,
                show: false // Não mostrar o painel de instruções
              });
              
              console.log('Controle de rota criado com sucesso');
            } catch (routingError) {
              console.error('Erro ao criar controle de rota:', routingError);
              setRouteError('Não foi possível criar o controle de rota. Exibindo uma linha reta entre os pontos.');
              setRouteLoading(false);
              return;
            }
            
            // Adicionar listeners de eventos para limpar o timeout quando a rota for calculada ou falhar
            routingControlRef.current.on('routesfound', () => {
              clearTimeout(routeTimeout);
              console.log('Rota calculada com sucesso');
              setRouteLoading(false);
              setRouteError('');
            });
            
            routingControlRef.current.on('routingerror', (e: any) => {
              clearTimeout(routeTimeout);
              console.error('Erro ao calcular rota:', e);
              setRouteLoading(false);
              setRouteError('Não foi possível calcular a rota automática. Exibindo uma linha reta entre os pontos.');
              
              // Já temos uma linha reta de backup, mas vamos garantir que ela seja visível
              console.log('Usando a linha reta de backup como alternativa');
              
              // Ajustar o zoom para mostrar todos os pontos
              try {
                if (mapInstanceRef.current) {
                  // Criar um bounds com todos os pontos
                  const bounds = L.latLngBounds(routePoints);
                  if (bounds.isValid() && typeof mapInstanceRef.current.fitBounds === 'function') {
                    mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
                    console.log('Zoom ajustado para mostrar todos os pontos');
                  }
                  
                  // Adicionar popups para cada ponto para melhorar a experiência do usuário
                  validWaypoints.forEach((waypoint, index) => {
                    try {
                      if (index === 0) {
                        // Ponto de partida
                        L.popup()
                          .setLatLng([waypoint.location.lat, waypoint.location.lng])
                          .setContent(`<b>Ponto de Partida</b><br>${waypoint.address || ''}`)
                          .openOn(mapInstanceRef.current);
                      } else if (index === validWaypoints.length - 1) {
                        // Último ponto
                        L.popup()
                          .setLatLng([waypoint.location.lat, waypoint.location.lng])
                          .setContent(`<b>Destino Final</b><br>${waypoint.title}<br>${waypoint.address || ''}`)
                          .addTo(mapInstanceRef.current);
                      }
                    } catch (popupError) {
                      console.warn(`Erro ao adicionar popup para waypoint ${index}:`, popupError);
                    }
                  });
                }
              } catch (fallbackError) {
                console.error('Erro ao implementar fallback para rota:', fallbackError);
              }
            });
            
            // Função para adicionar o controle de rota quando o mapa estiver pronto
            const addRouteControlToMap = () => {
              if (!mapInstanceRef.current || typeof mapInstanceRef.current.addControl !== 'function') {
                console.warn('Mapa não está disponível para adicionar o controle de rota');
                return false;
              }
              
              // Verificar se o controle de rota foi criado corretamente
              if (!routingControlRef.current) {
                console.warn('Controle de rota não foi criado corretamente');
                return false;
              }
              
              try {
                mapInstanceRef.current.addControl(routingControlRef.current);
                console.log('Controle de rota adicionado ao mapa');
                return true;
              } catch (controlError) {
                console.error('Erro ao adicionar controle de rota ao mapa:', controlError);
                return false;
              }
            };
            
            // Tentar adicionar o controle agora
            if (mapInitialized) {
              console.log('Mapa já está inicializado, adicionando controle de rota imediatamente');
              addRouteControlToMap();
            } else {
              console.log('Mapa ainda não está inicializado, aguardando...');
              
              // Configurar um observador para o estado de inicialização do mapa
              const checkMapInitialized = () => {
                if (mapInitialized) {
                  console.log('Mapa inicializado, adicionando controle de rota');
                  addRouteControlToMap();
                } else {
                  console.log('Mapa ainda não inicializado, tentando novamente em 500ms');
                  setTimeout(checkMapInitialized, 500);
                }
              };
              
              // Iniciar a verificação após um pequeno delay
              setTimeout(checkMapInitialized, 500);
            }
            
            // Ajustar o zoom para mostrar toda a rota
            if (routePoints.length > 0 && mapInstanceRef.current) {
              try {
                const bounds = L.latLngBounds(routePoints);
                if (bounds.isValid() && typeof mapInstanceRef.current.fitBounds === 'function') {
                  mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
                  console.log('Zoom ajustado para mostrar toda a rota');
                }
              } catch (boundsError) {
                console.warn('Erro ao ajustar zoom para os limites da rota:', boundsError);
              }
            }
          } catch (routeError) {
            console.error('Erro ao criar rota:', routeError);
            // Não falhar completamente se a rota não puder ser criada
            // Pelo menos os marcadores já foram adicionados
          }
        }
      } else {
        console.warn('Nenhum waypoint válido para exibir no mapa');
      }

      setLoading(false);
    } catch (err) {
      console.error('Erro ao inicializar o mapa:', err);
      // @ts-ignore
      setError(`Não foi possível carregar o mapa: ${err.message || 'Erro desconhecido'}`);
      setLoading(false);
    }

    // Limpar recursos ao desmontar o componente
    return () => {
      console.log('Desmontando componente de mapa, limpando recursos...');
      
      // Primeiro limpar os recursos que dependem do mapa
      cleanupMapResources();
      
      // Destruir o mapa com segurança
      if (mapInstanceRef.current) {
        try {
          // Verificar se o método remove existe
          if (typeof mapInstanceRef.current.remove === 'function') {
            console.log('Removendo mapa usando método remove()...');
            mapInstanceRef.current.remove();
          } 
          // Fallback para versões antigas do Leaflet
          // Usamos any para acessar propriedades internas do Leaflet
          else {
            console.log('Removendo mapa usando método alternativo...');
            const mapInstance = mapInstanceRef.current as any;
            
            // Remover listeners de eventos
            if (typeof mapInstanceRef.current.off === 'function') {
              mapInstanceRef.current.off();
            }
            
            // Limpar referências do container
            if (mapInstance && mapInstance._container) {
              const container = mapInstance._container as any;
              if (container && container._leaflet_id) {
                container._leaflet_id = null;
              }
            }
          }
        } catch (e) {
          console.warn('Erro ao destruir mapa:', e);
        } finally {
          // Garantir que a referência seja limpa
          mapInstanceRef.current = null;
        }
      }
    };
  }, [waypoints, mapCenter, zoom]);

  // Renderizar o componente
  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Se useSimpleMap for true, mostrar uma mensagem e desenhar uma linha reta */}
      {useSimpleMap ? (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Modo de visualização simplificada
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Mostrando apenas os pontos da rota sem cálculo de rota otimizada.
          </Typography>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={() => setUseSimpleMap(false)}
          >
            Tentar Mapa Avançado
          </Button>
        </Box>
      ) : (
        <>
          {/* Indicador de carregamento inicial do mapa */}
          {loading && (
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.7)',
              zIndex: 1000
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={60} />
                <Typography variant="body2" sx={{ mt: 2 }}>Carregando mapa...</Typography>
              </Box>
            </Box>
          )}
          
          {/* Indicador de carregamento da rota */}
          {!loading && routeLoading && (
            <Box sx={{ 
              position: 'absolute', 
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.85)',
              borderRadius: 2,
              boxShadow: 3,
              zIndex: 1000,
              p: 3
            }}>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ mt: 2 }}>Calculando rota...</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Isso pode levar alguns instantes
              </Typography>
            </Box>
          )}
          
          {/* Mensagem de erro do mapa */}
          {error && (
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.9)',
              zIndex: 1000,
              p: 2
            }}>
              <Box sx={{ maxWidth: '80%', textAlign: 'center', bgcolor: 'error.light', p: 3, borderRadius: 2 }}>
                <Typography variant="h6" color="error.contrastText" gutterBottom>Erro ao carregar mapa</Typography>
                <Typography color="error.contrastText" paragraph>{error}</Typography>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => setUseSimpleMap(true)}
                >
                  Usar Mapa Simplificado
                </Button>
              </Box>
            </Box>
          )}
          
          {/* Mensagem de erro da rota */}
          {!loading && !error && routeError && (
            <Box sx={{ 
              position: 'absolute', 
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: '90%',
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              borderRadius: 2,
              boxShadow: 3,
              zIndex: 900,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1
            }}>
              <Typography variant="body2">{routeError}</Typography>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => setUseSimpleMap(true)}
              >
                Usar Mapa Simplificado
              </Button>
            </Box>
          )}
          
          <div 
            ref={mapRef} 
            style={{ width: '100%', height: '100%', minHeight: '400px' }}
          />
        </>
      )}
    </Box>
  );
};

export default RouteMapComponent;